<script setup>
import { ref, computed, watch, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);
const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const inboxId = ref('');
const messageTemplate = ref(
  '📞 Chamada recebida\nTelefone: [TELEFONE]\nDiscado: [DISCADO]\nExtensão: [EXTENSAO]\nStatus: [STATUS]\nDuração: [DURACAO]'
);
const privateNote = ref(false);
const labelName = ref('');
const funnelId = ref('');
const funnelStageId = ref('');

const inboxes = computed(() => getters['inboxes/getInboxes'].value);
const labels = computed(() => getters['labels/getLabels'].value);
const funnels = computed(() => getters['funnels/getFunnels'].value);
const stages = computed(() => getters['funnels/getStages'].value);
const uiFlags = computed(() => getters['phoneCentralConfigs/getUIFlags'].value);

const isValid = computed(() => !!inboxId.value);

const onClose = () => emit('close');

watch(funnelId, newVal => {
  funnelStageId.value = '';
  if (newVal) {
    store.dispatch('funnels/fetchStages', newVal);
  }
});

const addConfig = async () => {
  try {
    await store.dispatch('phoneCentralConfigs/create', {
      inbox_id: inboxId.value,
      message_template: messageTemplate.value,
      private_note: privateNote.value,
      label_name: labelName.value,
      funnel_id: funnelId.value || null,
      funnel_stage_id: funnelStageId.value || null,
    });
    useAlert(t('PHONE_CENTRAL.ADD.API.SUCCESS_MESSAGE'));
    onClose();
  } catch (error) {
    useAlert(error?.message || t('PHONE_CENTRAL.ADD.API.ERROR_MESSAGE'));
  }
};

onBeforeMount(() => {
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  store.dispatch('funnels/fetchFunnels');
});
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('PHONE_CENTRAL.ADD.TITLE')"
      :header-content="$t('PHONE_CENTRAL.ADD.DESC')"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="addConfig">
      <div class="w-full mb-4">
        <label>
          {{ $t('PHONE_CENTRAL.FORM.INBOX.LABEL') }}
          <select v-model="inboxId" class="mb-0">
            <option value="" disabled>
              {{ $t('PHONE_CENTRAL.FORM.INBOX.PLACEHOLDER') }}
            </option>
            <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
              {{ inbox.name }}
            </option>
          </select>
        </label>
      </div>

      <div class="w-full mb-4">
        <label>
          {{ $t('PHONE_CENTRAL.FORM.MESSAGE.LABEL') }}
          <textarea
            v-model="messageTemplate"
            class="mb-0"
            rows="4"
            :placeholder="$t('PHONE_CENTRAL.FORM.MESSAGE.PLACEHOLDER')"
          />
        </label>
        <p class="text-xs text-n-slate-10 mt-1">
          {{ $t('PHONE_CENTRAL.FORM.MESSAGE.HELP') }}
        </p>
      </div>

      <div class="flex items-center w-full gap-2 mb-4">
        <input v-model="privateNote" type="checkbox" :value="true" />
        <label>
          {{ $t('PHONE_CENTRAL.FORM.PRIVATE.LABEL') }}
        </label>
      </div>

      <div class="w-full mb-4">
        <label>
          {{ $t('PHONE_CENTRAL.FORM.LABEL_SELECT.LABEL') }}
          <select v-model="labelName" class="mb-0">
            <option value="">
              {{ $t('PHONE_CENTRAL.FORM.LABEL_SELECT.PLACEHOLDER') }}
            </option>
            <option
              v-for="label in labels"
              :key="label.id"
              :value="label.title"
            >
              {{ label.title }}
            </option>
          </select>
        </label>
      </div>

      <div class="w-full mb-4">
        <label>
          {{ $t('PHONE_CENTRAL.FORM.FUNNEL.LABEL') }}
          <select v-model="funnelId" class="mb-0">
            <option value="">
              {{ $t('PHONE_CENTRAL.FORM.FUNNEL.PLACEHOLDER') }}
            </option>
            <option
              v-for="funnel in funnels"
              :key="funnel.id"
              :value="funnel.id"
            >
              {{ funnel.title }}
            </option>
          </select>
        </label>
        <p class="text-xs text-n-slate-10 mt-1">
          {{ $t('PHONE_CENTRAL.FORM.FUNNEL.HELP') }}
        </p>
      </div>

      <div v-if="funnelId" class="w-full mb-4">
        <label>
          {{ $t('PHONE_CENTRAL.FORM.FUNNEL_STAGE.LABEL') }}
          <select v-model="funnelStageId" class="mb-0">
            <option value="" disabled>
              {{ $t('PHONE_CENTRAL.FORM.FUNNEL_STAGE.PLACEHOLDER') }}
            </option>
            <option v-for="stage in stages" :key="stage.id" :value="stage.id">
              {{ stage.name }}
            </option>
          </select>
        </label>
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('PHONE_CENTRAL.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="$t('PHONE_CENTRAL.FORM.CREATE')"
          :disabled="!isValid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
